import { put, select, takeLatest, call, takeEvery, take } from 'redux-saga/effects';
import { getRecommendations } from '../services/launchpad-api';
import { getLearnerDashboard } from '../services/dashboard-api';
import { authSelectors } from '@rosetta/react-auth';
import { questionnaireActions, questionnaireSelectors } from '../../questionnaire/reducer/questionnaire-reducer';
import {
  INIT_RECOMMENDATIONS,
  INIT_DASHBOARD,
  LAUNCH_RECOMMENDATION,
  SEND_MOBILE_EMAIL,
  launchpadActions,
  EMAIL_STATUS,
  launchpadSelectors
} from '../reducer/launchpad-reducer';
import { push } from 'connected-react-router';
import { isCatalyst } from '../../../helpers/productConfigHelper';
import { LOGIN_STATUS } from '@rosetta/react-auth/dist/index';
import { getSamlEntityIdFromOrgId, getMobileLoginSessionData, serializeHappyUrl } from '../../login/services/sso-helpers';
import { deliverEmail, isValidEmail } from '../../app/util/email-helper';
import {
  appActions,
  appSelectors,
  LOG_LEVEL,
  SET_APP_INITIALIZED,
  TRANSACTION_STATE
} from '../../app/reducer/app-reducer';
import { emitEvent, DASHBOARD_VIEW, PRODUCT_LAUNCHED } from '../../../helpers/eventingHelper';
import { isMobile as isMobileDetected } from 'react-device-detect';
import { logBrazeEvent } from '../../../helpers/engagementHelper';
import { getTargetLanguageCode } from '../../app/util/user-helper';
import { isEndangeredLanguage } from '../../app/services/language-helpers';


export function* initLaunchpad() {
  // set flag that recommendations are not loaded
  yield put(launchpadActions.setRecommendationsLoaded(false));

  // wait until the app is initialized and any stored login has reprocessed
  const isAppInitialized = yield select(appSelectors.isAppInitialized);
  if (!isAppInitialized) {
    yield take(SET_APP_INITIALIZED);
  }
  // if we don't have a user we don't have a recommendation
  const loginStatus = yield select(authSelectors.loginStatus);

  let authToken = yield select(authSelectors.authToken);
  const targetLanguageCode = yield select(authSelectors.targetLanguageCode);
  if (loginStatus !== LOGIN_STATUS.LOGGED_IN || !targetLanguageCode) {
    return null;
  }

  const userName = yield select(authSelectors.userName);
  const interfaceLanguage = yield select(authSelectors.userInterfaceLanguage);
  try {
    const recommendations = yield call(getRecommendations, userName, interfaceLanguage, targetLanguageCode, authToken);
    const userInfo = yield select(authSelectors.userInfo);

    yield put(launchpadActions.setRecommendations(recommendations, userInfo));
  } catch (error) {
    yield put(appActions.setLastTransactionState(TRANSACTION_STATE.FAIL, JSON.parse(error.message).error));
    yield put(push('launchpad/error'));
  }
}

function* initDashboard() {
  const authToken = yield select(authSelectors.authToken);
  const userId = yield select(authSelectors.userId);
  const userInfo = yield select(authSelectors.userInfo);
  if (!isEndangeredLanguage(getTargetLanguageCode(userInfo))) {
    yield put(launchpadActions.setDashboardLoading(true));
    const learnerDashboard = yield call(getLearnerDashboard, userId, authToken);
    yield put(launchpadActions.setDashboardLoading(false));

    if (learnerDashboard) {
      yield put(launchpadActions.setDashboard(learnerDashboard));
      if (learnerDashboard.showDashboard) {
        yield call(emitEvent, DASHBOARD_VIEW, null, userInfo);
      }
    }
  }
}

let launching = false;
function* launchRecommendationSaga(action) {
  const QUESTIONNAIRE_PATH = '/objectives/start';
  let recommendation = action.payload.recommendation;

  if (!launching) {
    // This turns out to be key thing to be done to ensure new recommendations if/when we return to lp.
    yield put(launchpadActions.setRecommendationsLoaded(false));
    launching = true;
    // load the Catalyst Pretest
    let link = recommendation.recommendation;
    let mobile_link = recommendation.mobile_recommendation;
    const targetLanguageCode = yield select(authSelectors.targetLanguageCode);

    // currently unsupporting mobile placement tests in Dutch, Italian, or Chinese
    const unsupportedMobileTestLangs = ['nl-NL', 'it-IT', 'zh-CN'];
    if (
      isCatalyst(recommendation.product_id) &&
      recommendation.category === 'LTS' &&
      !recommendation.related_product_id &&
      isMobileDetected &&
      unsupportedMobileTestLangs.includes(targetLanguageCode)
    ) {
      // mobile assessments are unsupported for dutch, italian, and chinese
      yield put(push({ pathname: 'launchpad/error', state: { msg: 'unsupportedAssessments' } }));
    } else if (
      isCatalyst(recommendation.product_id) &&
      recommendation.category === 'LTS' &&
      !recommendation.related_product_id
    ) {
      // Catalyst launch into assessment test
      // If the user has the option to skipPlacementTest, allow them to now skip it
      const skipPlacementTest = yield select(questionnaireSelectors.skipPlacementTest);
      if (skipPlacementTest) {
        // Turn the lights back on, not sure about doing this...
        yield put(launchpadActions.setRecommendationsLoaded(true));
        yield put(launchpadActions.setSkipPlacementPopup(true, recommendation));
      } else {
        yield call(emitEvent, PRODUCT_LAUNCHED, { url: link });
        console.log('launchpad-sagas:128 location.assign');
        window.location.assign(link);
      }
    } else if (isCatalyst(recommendation.product_id) && recommendation.category === 'questionnaire') {
      // Catalyst onboarding
      const pathComponents = recommendation.recommendation.split('/');
      const activityId = pathComponents && pathComponents.length === 5 ? pathComponents[4] : '';
      yield put(questionnaireActions.storeActivityId(activityId));
      yield call(emitEvent, PRODUCT_LAUNCHED, { url: QUESTIONNAIRE_PATH });
      yield put(push(QUESTIONNAIRE_PATH));
    } else if (action.payload.isMobile) {
      if (mobile_link.includes('unsupported')) {
        yield put(push({ pathname: 'launchpad/error', state: { msg: mobile_link } }));
      } else {
        yield call(emitEvent, PRODUCT_LAUNCHED, { url: mobile_link });
        console.log('launchpad-sagas:143 location.assign');
        const mobileLoginSessionData = getMobileLoginSessionData();
        // mobile_link is NOT the right launch link for mobile sso
        if (mobileLoginSessionData && mobileLoginSessionData.token) {
          window.location.assign(serializeHappyUrl(mobileLoginSessionData));
        } else {
          console.log('sending to mobile_link: ' + mobile_link);
          window.location.assign(mobile_link);
        }
      }
    } else {
      yield call(emitEvent, PRODUCT_LAUNCHED, { url: link });
      console.log('launchpad-sagas:148 location.assign');
      window.location.assign(link);
    }
    launching = false;
  }
}

function* sendMobileEmailSaga(action) {
  const email = action.payload.emailToSendTo;
  if (!isValidEmail(email)) {
    return yield put(launchpadActions.setMobileEmailStatus(EMAIL_STATUS.INVALID));
  }
  yield put(launchpadActions.setMobileEmailStatus(EMAIL_STATUS.SEND_ATTEMPTED));
  const userInfo = yield select(authSelectors.userInfo);
  let orgSamlEntityId = null;
  try {
    orgSamlEntityId = yield call(getSamlEntityIdFromOrgId, userInfo.organization);
  } catch (ex) {
    // it is OK - if there is no samlEntityId for the org it throws
  }
  const emailLauncherType = yield select(launchpadSelectors.mobileEmailLauncherType);
  let trigger = 'MobileAppConsolidatedTrigger';
  let isSSO = orgSamlEntityId ? true : false;
  let product = '';

  switch (emailLauncherType) {
    case 'Foundations':
      product = 'foundations';
      break;
    case 'Fluency Builder':
      product = 'fluency';
      break;
    default:
      yield put(
        appActions.logEvent({
          level: LOG_LEVEL.ERROR,
          message: 'unknown emailLauncherType ' + emailLauncherType + ' for user ' + userInfo.gui
        })
      );
      return yield put(launchpadActions.setMobileEmailStatus(EMAIL_STATUS.SENT_UNSUCCESSFULLY));
  }
  const token = yield select(authSelectors.authToken);
  try {
    yield call(deliverEmail, email, userInfo, trigger, token, isSSO, product);
  } catch (ex) {
    return yield put(launchpadActions.setMobileEmailStatus(EMAIL_STATUS.SENT_UNSUCCESSFULLY));
  }

  // Notify the engagement service of this request
  logBrazeEvent('RequestedLinkToMobileApp', {});

  return yield put(launchpadActions.setMobileEmailStatus(EMAIL_STATUS.SENT_SUCCESSFULLY));
}

export function* launchpadSagas() {
  yield takeLatest(INIT_RECOMMENDATIONS, initLaunchpad);
  yield takeLatest(INIT_DASHBOARD, initDashboard);
  yield takeEvery(LAUNCH_RECOMMENDATION, launchRecommendationSaga);
  yield takeEvery(SEND_MOBILE_EMAIL, sendMobileEmailSaga);
}



// WEBPACK FOOTER //
// ./src/modules/launchpad/sagas/launchpad-sagas.js